import { Link } from "react-router-dom";

import { SOCIALS } from "@/config/site";

import SocialIcon from "./SocialIcons";
import { logoFor } from "./content";


/**
 * The canvas's `Footer` screen.
 *
 * Darker than the band above it (`--footer` against `--band`), which is what
 * separates the two dark bands without a rule between them.
 *
 * The canvas drew every link as an in-page anchor, because it was a one-page
 * design — "Privacy Policy" pointed at `#faq` and LinkedIn at `#contact`. Now
 * that these pages all exist and share this footer, each goes to its real
 * route, and the social destinations come from config/site.ts.
 *
 * The Connect column's lone LinkedIn text link became a row of icon tiles when
 * four more networks joined it: five stacked text links would have outgrown the
 * column and read as navigation rather than as a social row.
 */

interface FooterColumn {
  heading: string;
  links: Array<{ to: string; label: string }>;
}

const COLUMNS: FooterColumn[] = [
  {
    heading: "Platform",
    links: [
      { to: "/", label: "Home" },
      { to: "/about", label: "About Us" },
      { to: "/solutions", label: "Solutions" },
      { to: "/pricing", label: "Pricing" },
      { to: "/contact", label: "Contact Us" },
    ],
  },
  {
    heading: "Resources",
    links: [
      { to: "/security", label: "Security" },
      { to: "/privacy", label: "Privacy Policy" },
      { to: "/terms", label: "Terms of Service" },
    ],
  },
];

const headingStyle: React.CSSProperties = {
  fontFamily: "'Manrope', sans-serif",
  fontSize: 11,
  letterSpacing: ".16em",
  textTransform: "uppercase",
  /* The accent, matching the social tiles below them. These are the only
     headings in the footer, so colouring them apart from the links underneath
     is what separates the columns without a rule; on the ink ramp they were a
     fourth shade of the same grey and the three columns ran together.
     Measured: 6.34:1 on the light footer, 13.3:1 on the dark one. */
  color: "var(--band-acc)",
  marginBottom: 4,
};

const linkStyle: React.CSSProperties = {
  fontSize: 13.5,
  color: "rgba(var(--band-ink-rgb),.72)",
};

const columnStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

/**
 * A square carrying the accent: 34px, and 40px on a phone so it is a
 * comfortable tap target. The size is on `.site-footer-tile` in global.css,
 * because an inline size cannot change at a breakpoint (T101026002).
 *
 * Band tokens, not literals. This footer used to be dark in both themes, which
 * is why these were hard-coded light values; it follows the theme now, so they
 * resolve with it.
 */
const socialTileStyle: React.CSSProperties = {
  borderRadius: 9,
  border: "1px solid rgba(var(--band-acc-rgb),.3)",
  background: "rgba(var(--band-acc-rgb),.07)",
  color: "var(--band-acc)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "border-color .2s, background .2s, color .2s",
};

/**
 * `dark` arrives as a PROP rather than through useDcTheme, for the same
 * reason Nav takes one: DcPage imports this file, so reading the context
 * from here would close an import cycle.
 */
export default function Footer({ dark }: { dark: boolean }) {
  return (
    <footer
      style={{
        background: "var(--footer)",
        color: "var(--band-ink)",
        borderTop: "1px solid rgba(var(--band-line-rgb),.08)",
      }}
    >
      <div
        className="site-gutter"
        style={{ maxWidth: 1240, margin: "0 auto", paddingTop: 72, paddingBottom: 36 }}
      >
        {/* Four tracks from tablet up; on a phone the brand and Connect take
            the full width and the two link columns share a row (T101026002). */}
        <div className="site-footer-grid">
          <div>
            {/* The real mark, not the ✦ glyph that stood in for it. It takes
                the master for the GROUND, which follows the theme now, so this
                passes `dark` rather than the `true` it used to. */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 700,
                fontSize: 17,
                marginBottom: 14,
              }}
            >
              <img
                src={logoFor(dark)}
                alt=""
                aria-hidden="true"
                width={22}
                height={22}
                style={{ display: "block", flex: "none" }}
              />
              AI2Aim
            </div>
            <p
              style={{
                margin: 0,
                fontSize: 13.5,
                color: "rgba(var(--band-ink-rgb),.66)",
                maxWidth: 300,
              }}
            >
              AI-powered SaaS platform unifying HR, accounting, and work
              management with intelligent automation and HiveMind collective
              intelligence.
            </p>
          </div>

          {COLUMNS.map((col) => (
            <div key={col.heading} className="site-footer-links">
              <span style={headingStyle}>{col.heading}</span>
              {col.links.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  className="dc-h-cyan"
                  style={linkStyle}
                >
                  {l.label}
                </Link>
              ))}
            </div>
          ))}

          <div style={columnStyle}>
            <span style={headingStyle}>Connect</span>
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginBottom: 4,
              }}
            >
              {SOCIALS.map((s) =>
                s.href ? (
                  <a
                    key={s.key}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer noopener"
                    aria-label={s.label}
                    title={s.label}
                    className="dc-h-social site-footer-tile"
                    style={socialTileStyle}
                  >
                    <SocialIcon name={s.key} />
                  </a>
                ) : (
                  /* No account yet. Rendered as a non-interactive tile rather
                     than a link to "#": an anchor with a dead href is focusable
                     and announces as a link, promising a destination that does
                     not exist. This keeps the row's shape without the lie. */
                  <span
                    key={s.key}
                    aria-hidden="true"
                    title={`${s.label} — coming soon`}
                    className="site-footer-tile"
                    style={{ ...socialTileStyle, opacity: 0.35 }}
                  >
                    <SocialIcon name={s.key} />
                  </span>
                ),
              )}
            </div>
            <a
              href="mailto:contact@ai2aim.ai"
              className="dc-h-cyan"
              style={linkStyle}
            >
              contact@ai2aim.ai
            </a>
            <div
              style={{
                marginTop: 12,
                fontFamily: "'Manrope', sans-serif",
                fontSize: 11.5,
                lineHeight: 1.9,
                color: "rgba(var(--band-ink-rgb),.66)",
              }}
            >
              infra: aws-ca-central-1
              <br />
              enc: AES-256-GCM
              <br />
              auth: OAuth 2.0 + RBAC
            </div>
          </div>
        </div>

        <div
          style={{
            borderTop: "1px solid rgba(var(--band-line-rgb),.08)",
            paddingTop: 24,
            display: "flex",
            justifyContent: "space-between",
            gap: 20,
            flexWrap: "wrap",
            fontSize: 12.5,
            color: "rgba(var(--band-ink-rgb),.66)",
          }}
        >
          <span>&copy; 2025 AI2Aim Inc. All rights reserved.</span>
          <span
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: 11,
              letterSpacing: ".12em",
              textTransform: "uppercase",
            }}
          >
            Aim. Command &amp; Execute.
          </span>
        </div>
      </div>
    </footer>
  );
}
